import { useEffect } from "react";
import { useLocation } from "react-router-dom";
// The slim index, not caseStudies.js: this component is always mounted, so
// whatever it imports lands in the main bundle.
import { getCaseStudySummary } from "../../data/caseStudyIndex";

// Keeps <title>, the meta description, the canonical link and og:url/og:title in
// step with the current route.
//
// public/index.html deliberately ships NO <link rel="canonical">: Create React App
// serves that one file for every path, so a hardcoded canonical would tell Google
// that /work/supplier-data-console is a duplicate of the homepage. This component writes the
// correct value at runtime instead. Google executes JavaScript, so the runtime
// value is what gets indexed - the non-JS social crawlers (LinkedIn, WhatsApp,
// Slack) still fall back to the static tags in index.html, which is why those
// stay accurate for the site as a whole.
//
// Mirrors the router-child pattern of ScrollToTop: render it inside <Router>,
// it renders nothing.

const ORIGIN = "https://findhari.com";
const NAME = "Haribabu Manoharan";

// Must match the <title> and description in public/index.html, so an unknown
// route looks identical to a cold page load.
const DEFAULT_TITLE = `${NAME} | Senior Product Designer & Frontend Engineer`;
const DEFAULT_DESCRIPTION =
  "Senior product designer and frontend engineer with 10 years building data-dense enterprise software. Design systems, React, TypeScript. Procurement and manufacturing platforms for a global semiconductor equipment manufacturer, and for NEOM.";

const STATIC_ROUTES = {
  "/": { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION },
  "/work": {
    title: `Work & Case Studies | ${NAME}`,
    description:
      "Selected case studies: enterprise procurement, supplier evaluation and manufacturing-change platforms, plus EdTech and consumer products built end to end.",
  },
  "/live-demos": {
    title: `Live Demos | ${NAME}`,
    description:
      "Five enterprise procurement and supplier-data apps I designed and built, running live in the browser on anonymized sample data. No sign-up.",
  },
  "/ux-writing": {
    title: `UX Writing & Content Design | ${NAME}`,
    description:
      "UX writing and content design practice: microcopy, content systems, accessibility and voice & tone principles, plus before/after writing examples.",
  },
  "/writing-samples": {
    title: `Writing Samples | ${NAME}`,
    description:
      "UX writing samples organized by problem type: microcopy, content design, AI UX and product writing. Context, user need, original, rewrite and reasoning for each.",
  },
  "/about": {
    title: `About | ${NAME}`,
    description:
      "Architecture graduate from NIT Trichy turned product designer and frontend engineer, building data-dense tools for complex enterprise workflows.",
  },
  "/resume": {
    title: `Resume | ${NAME}`,
    description:
      "Experience, skills and education for Haribabu Manoharan - senior product designer and frontend engineer. Downloadable PDF.",
  },
  "/contact": {
    title: `Contact | ${NAME}`,
    description:
      "Get in touch with Haribabu Manoharan about product design and frontend engineering roles.",
  },
};

// Routes that must never be indexed. The value is rewritten on EVERY navigation,
// not just on these paths: this is a single-page app, so a noindex set once would
// otherwise persist in the live DOM as the visitor navigates on to a public page.
//
// Empty since /admin (the demo-gate approval console) was removed along with the
// gate itself. Kept rather than deleted: the list is the mechanism, and the
// rewrite-on-every-navigation behaviour above is what makes adding a route here
// safe. Deleting it would mean rediscovering that subtlety next time.
const NOINDEX_ROUTES = [];
// Must match the robots meta in public/index.html.
const DEFAULT_ROBOTS = "index, follow, max-image-preview:large";

// Strip a trailing slash so "/work/" and "/work" resolve to one canonical URL.
// The homepage keeps its slash, since ORIGIN + "" would be a bare origin.
const normalize = (pathname) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, "") : "/";

const resolve = (pathname) => {
  const path = normalize(pathname);

  if (STATIC_ROUTES[path]) {
    return { ...STATIC_ROUTES[path], canonicalPath: path };
  }

  const slug = path.startsWith("/work/") ? path.slice("/work/".length) : null;
  const study = slug ? getCaseStudySummary(slug) : null;
  if (study) {
    return {
      title: `${study.subtitle || study.title} | ${NAME}`,
      description: study.description || DEFAULT_DESCRIPTION,
      // study.slug, not the URL slug: legacy alias URLs must point their
      // canonical at the renamed page rather than at themselves.
      canonicalPath: `/work/${study.slug}`,
    };
  }

  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonicalPath: path,
  };
};

// Update an existing head tag, or create it if index.html did not ship one.
const upsert = (selector, create, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setMeta = (name, attr, content) =>
  upsert(
    `meta[${attr}="${name}"]`,
    () => {
      const el = document.createElement("meta");
      el.setAttribute(attr, name);
      return el;
    },
    "content",
    content
  );

const RouteMeta = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const { title, description, canonicalPath } = resolve(pathname);
    const url = `${ORIGIN}${canonicalPath === "/" ? "/" : canonicalPath}`;

    document.title = title;
    setMeta(
      "robots",
      "name",
      NOINDEX_ROUTES.includes(normalize(pathname))
        ? "noindex, nofollow"
        : DEFAULT_ROBOTS
    );
    setMeta("description", "name", description);
    setMeta("og:title", "property", title);
    setMeta("og:description", "property", description);
    setMeta("og:url", "property", url);
    setMeta("twitter:title", "name", title);
    setMeta("twitter:description", "name", description);

    upsert(
      'link[rel="canonical"]',
      () => {
        const el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        return el;
      },
      "href",
      url
    );
  }, [pathname]);

  return null;
};

export default RouteMeta;
